import React, { useCallback, useEffect, useState } from 'react'
import Select from 'react-select'

import { httpOk, GET } from 'helpers/api'
import { formatDate } from 'helpers/helpers'

import usePosMonitor from 'pages/admin/sql/usePosMonitor'
import ChequesTab from 'pages/admin/sql/ChequesTab'
import EventsTab from 'pages/admin/sql/EventsTab'
import SqlTab from 'pages/admin/sql/SqlTab'
import { buildSchema, chequesSummaryQuery, schemaQuery } from 'pages/admin/sql/queries'

/**
 * Монитор кассы.
 *
 * Задача, ради которой он существует: агрегатор говорит «заказ отправлен», а
 * проверить это можно было только подключившись к кассе через AnyDesk. Здесь
 * обе стороны видны рядом — что облако отправило в канал точки (вкладка
 * «События») и что после этого реально лежит в локальной базе кассы
 * (вкладка «Чеки»). Расхождение между ними и есть ответ.
 *
 * Экран общий для админки и партнёрского раздела: разница только в ручках и в
 * доступе к сырой базе кассы. Кого куда пускать, решает бэк — партнёру список
 * логинов и точек приходит уже урезанным до его клиентов.
 *
 * allowSql = false (партнёрский раздел) — вкладок «События» и «SQL» нет вовсе:
 * произвольные запросы в базу клиента партнёр отправлять не должен, ему
 * остаётся готовый разбор на вкладке «Чеки».
 */
function PosMonitor({
	basePath = '/admin',
	usersUrl = '/admin/helper/users',
	posesUrl = '/admin/sql/poses',
	allowWrite = true,
	allowSql = true,
}) {
	const monitor = usePosMonitor(basePath)
	// Стабильная ссылка на отправку запроса: сам monitor — новый объект на
	// каждый рендер, и эффект, завязанный на него, крутился бы без остановки.
	const query = monitor.query

	const [users, setUsers] = useState([])
	const [poses, setPoses] = useState([])
	const [selectedUser, setSelectedUser] = useState(null)
	const [selectedPos, setSelectedPos] = useState(null)

	/** Точка, к которой реально подключены (не та, что выбрана в списке). */
	const [session, setSession] = useState(null)
	const [schema, setSchema] = useState(null)
	const [summary, setSummary] = useState(null)
	const [schemaError, setSchemaError] = useState(null)
	const [schemaLoading, setSchemaLoading] = useState(false)

	const [tab, setTab] = useState('cheques')

	async function getUsers() {
		const response = await GET(usersUrl, {}, { loader: true })
		if (httpOk(response)) setUsers(response.data)
	}

	async function getPoses(login) {
		const response = await GET(posesUrl, { 'login': login }, { loader: true })
		if (httpOk(response)) setPoses(response.data)
	}

	function handleSelectUser(option) {
		setSelectedUser(option)
		setSelectedPos(null)
		setPoses([])
		if (option?.login) getPoses(option.login)
	}

	function handleConnect() {
		if (!selectedPos || !selectedUser) return

		setSchema(null)
		setSummary(null)
		setSchemaError(null)
		setSession({ 'pos': selectedPos, 'login': selectedUser.login })
		monitor.connect(selectedPos.id, selectedUser.login)
	}

	function handleDisconnect() {
		monitor.disconnect()
		setSession(null)
		setSchema(null)
		setSummary(null)
		setSchemaError(null)
	}

	/**
	 * Схема читается один раз на подключение: запросы монитора собираются из
	 * тех колонок, что есть именно на этой версии кассы. Без этого запрос с
	 * «новой» колонкой на старой кассе падает, а старая касса про ошибку молчит.
	 */
	const loadSchema = useCallback(async () => {
		setSchemaLoading(true)
		setSchemaError(null)

		const schemaResult = await query(schemaQuery())

		if (schemaResult.error) {
			setSchemaError(schemaResult.error)
			setSchemaLoading(false)
			return
		}

		const nextSchema = buildSchema(schemaResult.rows)
		setSchema(nextSchema)

		const summaryResult = await query(chequesSummaryQuery(nextSchema))
		if (!summaryResult.error) setSummary(summaryResult.rows?.[0] || null)

		setSchemaLoading(false)
	}, [query])

	useEffect(() => {
		getUsers()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		if (monitor.connected && session && !schema && !schemaError && !schemaLoading) loadSchema()
	}, [monitor.connected, session, schema, schemaError, schemaLoading]) // eslint-disable-line react-hooks/exhaustive-deps

	const CONNECTION_STATUSES = {
		'idle': { 'text': 'Не подключено', 'className': 'bg-secondary' },
		'connecting': { 'text': 'Подключение…', 'className': 'bg-warning' },
		'connected': { 'text': 'Сокет подключён', 'className': 'bg-success' },
		'disconnected': { 'text': 'Соединение потеряно', 'className': 'bg-danger' },
		'error': { 'text': 'Ошибка соединения', 'className': 'bg-danger' },
	}
	const connectionStatus = CONNECTION_STATUSES[monitor.status] || CONNECTION_STATUSES['idle']

	return (
		<>
			{/* align-items-center, а не vertical-center: последний задаёт
			    flex-direction: column, и бейджи вставали друг на друга. */}
			<div className="page-header d-flex justify-content-between align-items-center">
				<h4 className="mb-0">
					Монитор кассы
				</h4>
				<div className="d-flex gap-2 align-items-center">
					<span className="text-muted fz12 text-nowrap"
						title="Сокет-сервер, к которому подключён монитор">
						{monitor.wsHost}
					</span>
					<span className={`badge text-nowrap ${connectionStatus.className}`}>
						{connectionStatus.text}
					</span>
					{session &&
						<span className={`badge text-nowrap ${monitor.posOnline ? 'bg-success' : 'bg-danger'}`}
							title={monitor.posOnline
								? 'Касса присутствует в канале бренда'
								: 'В канале бренда кассы нет. Проверьте, что монитор подключён к тому же сокет-серверу, что и касса'}>
							{monitor.posOnline ? 'Касса онлайн' : 'Касса офлайн'}
						</span>
					}
				</div>
			</div>

			<div className="card mb-3">
				<div className="card-body">
					<div className="row">
						<div className="form-group col-md-3">
							<label>Логин</label>
							<Select
								options={users}
								value={selectedUser}
								onChange={(option) => handleSelectUser(option)}
								isDisabled={!!session}
								placeholder=""
								noOptionsMessage={() => 'Список пуст'}
								getOptionLabel={(option) => `${option.login} — ${option.first_name || option.name || ''}`}
								getOptionValue={(option) => option.id}
							/>
						</div>
						<div className="form-group col-md-3">
							<label>Точка</label>
							<Select
								options={poses}
								value={selectedPos}
								onChange={(option) => setSelectedPos(option)}
								isDisabled={!!session || !selectedUser}
								placeholder=""
								noOptionsMessage={() => 'Список пуст'}
								getOptionLabel={(option) => option.name}
								getOptionValue={(option) => option.id}
							/>
						</div>
						<div className="form-group col-md-3">
							{/* Пустой label держит кнопку на одной линии с полями:
							    у react-select высота 38px, у голой кнопки — своя. */}
							<label>&nbsp;</label>
							{!session &&
								<button type="button" className="btn btn-primary w-100"
									style={{ 'height': '38px' }}
									disabled={!selectedPos}
									onClick={() => handleConnect()}>
									Подключиться
								</button>
							}
							{session &&
								<button type="button" className="btn btn-outline-danger w-100"
									style={{ 'height': '38px' }}
									onClick={() => handleDisconnect()}>
									Отключиться
								</button>
							}
						</div>
						<div className="col-md-3">
							{session &&
								<div className="fz14">
									<div className="d-flex justify-content-between">
										<span className="text-muted">Версия кассы</span>
										<span>{session.pos.desktop_client_version || '—'}</span>
									</div>
									<div className="d-flex justify-content-between">
										<span className="text-muted">Последний чек в облаке</span>
										<span>
											{session.pos.last_cheque_date
												? formatDate(session.pos.last_cheque_date, 'dd.MM.yyyy HH:mm')
												: '—'}
										</span>
									</div>
									<div className="d-flex justify-content-between">
										<span className="text-muted">Открытых чеков</span>
										<span>{summary ? summary.opened_count : '—'}</span>
									</div>
									<div className="d-flex justify-content-between">
										<span className="text-muted">Время на кассе</span>
										<span>
											{summary?.pos_time
												? formatDate(summary.pos_time, 'dd.MM.yyyy HH:mm')
												: '—'}
										</span>
									</div>
								</div>
							}
						</div>
					</div>

					{schemaLoading &&
						<div className="alert alert-info mb-0 mt-2 py-1">
							Читаю схему базы кассы…
						</div>
					}

					{schemaError &&
						<div className="alert alert-danger mb-0 mt-2">
							<div>Схему кассы прочитать не удалось: {schemaError}.</div>
							<div className="fz14 mt-1">
								Команда до кассы не дошла. Две обычные причины: касса выключена
								или не в сети — или монитор подключён к другому сокет-серверу
								(сейчас <b>{monitor.wsHost}</b>), а касса к боевому.
								{!monitor.posOnline &&
									<> Кассы нет и в канале бренда, так что вероятнее второе.</>
								}
							</div>
							<button type="button" className="btn btn-sm btn-outline-danger mt-2"
								onClick={() => { setSchemaError(null) }}>
								Повторить
							</button>
						</div>
					}
				</div>
			</div>

			{allowSql &&
				<ul className="nav nav-tabs mb-3">
					<li className="nav-item">
						<button type="button"
							className={`nav-link ${tab === 'cheques' ? 'active' : ''}`}
							onClick={() => setTab('cheques')}>
							Чеки
						</button>
					</li>
					<li className="nav-item">
						<button type="button"
							className={`nav-link ${tab === 'events' ? 'active' : ''}`}
							onClick={() => setTab('events')}>
							События
							{monitor.events.length > 0 &&
								<span className="badge bg-primary ms-1">{monitor.events.length}</span>
							}
						</button>
					</li>
					<li className="nav-item">
						<button type="button"
							className={`nav-link ${tab === 'sql' ? 'active' : ''}`}
							onClick={() => setTab('sql')}>
							SQL
						</button>
					</li>
				</ul>
			}

			{!session &&
				<div className="card">
					<div className="card-body text-center text-muted">
						Выберите логин и точку, затем нажмите «Подключиться».
					</div>
				</div>
			}

			{session && tab === 'cheques' &&
				<ChequesTab monitor={monitor} schema={schema} schemaLoading={schemaLoading} />
			}

			{session && allowSql && tab === 'events' &&
				<EventsTab monitor={monitor} />
			}

			{session && allowSql && tab === 'sql' &&
				<SqlTab monitor={monitor} allowWrite={allowWrite} />
			}
		</>
	)
}

export default PosMonitor
